import Color from "colorjs.io";
import chroma from "chroma-js";
import {
  getNamedColorEntries,
  findNamedColorByName as datasetFindNamedColorByName,
  findNamedColorsByHex as datasetFindNamedColorsByHex,
  searchNamedColorEntries,
  type NamedColorEntry,
  type NamedColorSource as DatasetNamedColorSource,
  type NamedColorSearchOptions as DatasetNamedColorSearchOptions,
} from "./namedColorDB";
import {
  semanticColorSearch,
  type SemanticColorQueryOptions,
  type SemanticColorSuggestion,
} from "./semanticColorSearch";

export type NamedColorSource = DatasetNamedColorSource;

export interface NamedColorInfo {
  id: string;
  name: string;
  displayName: string;
  hex: string;
  source: NamedColorSource;
}

export interface NamedColorMatch extends NamedColorInfo {
  deltaE: number;
}

export interface NamedColorSearchItem extends NamedColorInfo {
  score: number;
  exact: boolean;
}

export interface NamedColorListOptions {
  sources?: NamedColorSource[];
}

export interface NamedColorSearchOptions extends NamedColorListOptions {
  limit?: number;
}

export type DeltaEMethod = "lab" | "oklab" | "76" | "94" | "2000";
export type Illuminant = "D50" | "D65";

export interface NamedColorNearestOptions extends NamedColorListOptions {
  limit?: number;
  method?: DeltaEMethod;
}

export type NamedColorLookupOptions = NamedColorListOptions;

export interface CMYK {
  c: number;
  m: number;
  y: number;
  k: number;
}

export interface PrintProofMetrics {
  tac: number;
  maxChannel: number;
  softProofHex?: string;
  softProofDeltaE?: number;
  warnings: string[];
}

export interface PrintSuggestion {
  profileId: string;
  profileLabel: string;
  deltaE: number;
  proofHex?: string;
  tac: number;
  warnings: string[];
}

export interface CmykProfileResult {
  id: string;
  label: string;
  values: CMYK;
  metrics: PrintProofMetrics;
}

export interface ColorResult {
  hex: string;
  binary: string;
  rgb: { r: number; g: number; b: number; a?: number };
  hsl: { h: number; s: number; l: number; a?: number };
  hsv: { h: number; s: number; v: number; a?: number };
  hwb: { h: number; w: number; b: number; a?: number };
  cmyk: CMYK;
  cmykMetrics: PrintProofMetrics;
  cmykProfiles: CmykProfileResult[];
  printSuggestions: PrintSuggestion[];
  lab: { l: number; a: number; b: number };
  luv: { l: number; u: number; v: number };
  lch: { l: number; c: number; h: number };
  lchuv: { l: number; c: number; h: number };
  hunterLab: { l: number; a: number; b: number };
  xyz: { x: number; y: number; z: number };
  xyY: { x: number; y: number; Y: number };
  ycbcr: { y: number; cb: number; cr: number };
  oklab: { l: number; a: number; b: number };
  oklch: { l: number; c: number; h: number };
  displayP3?: string;
  rec2020?: string;
  nearestNamed?: NamedColorMatch;
  nearestNamedAlt?: NamedColorMatch[];
  inGamut: {
    srgb: boolean;
    p3: boolean;
    rec2020: boolean;
  };
}

export interface CmykProfilePlugin {
  id: string;
  label: string;
  transform: (color: Color) => CMYK;
}

export interface TintsShades {
  tints: string[];
  shades: string[];
}

export interface Harmonies {
  complementary: string[];
  analogous: string[];
  triadic: string[];
  tetradic: string[];
  splitComplementary: string[];
}

export type HarmonyType =
  | "analogous"
  | "complementary"
  | "triadic"
  | "tetradic"
  | "splitComplementary";

export interface DeltaEComparison {
  name: string;
  color: string;
  deltaE: number;
}

export interface ColorEncyclopediaData {
  baseHex: string;
  tints: string[];
  shades: string[];
  tones: string[];
  harmonies: Harmonies | null;
  deltaEComparisons: DeltaEComparison[];
  gamut: { id: "srgb" | "p3" | "rec2020"; label: string; inGamut: boolean }[];
}

export interface ColorComparisonResult {
  color1: string;
  color2: string;
  deltaE76: number;
  deltaE94: number;
  deltaE2000: number;
  illuminantSimulations: {
    d50: { color1: string; color2: string };
    d65: { color1: string; color2: string };
  };
}

export type DetectedFormat =
  | "hex"
  | "rgb"
  | "rgba"
  | "hsl"
  | "hsla"
  | "hsv"
  | "hwb"
  | "lab"
  | "lch"
  | "oklab"
  | "oklch"
  | "cmyk"
  | "css-named"
  | "pantone"
  | "p3"
  | "rec2020"
  | "unknown";

export interface ColorParseResult {
  success: boolean;
  result: ColorResult | null;
  detectedFormat: DetectedFormat;
  normalizedInput: string;
  suggestions?: string[];
  error?: string;
}

const MAX_NEAREST_RESULTS = 6;
const DEFAULT_NEAREST_LIMIT = 4;
const DEFAULT_SECONDARY_LIMIT = 5;

export class ColorEngine {
  private memoCache = new Map<string, ColorResult>();
  private cmykProfilePlugins: Map<string, CmykProfilePlugin> = new Map();

  registerCmykProfilePlugin(plugin: CmykProfilePlugin) {
    if (!plugin.id.trim()) {
      throw new Error("CMYK profile plugin id must be provided");
    }
    this.cmykProfilePlugins.set(plugin.id, plugin);
    this.memoCache.clear();
  }

  unregisterCmykProfilePlugin(id: string) {
    this.cmykProfilePlugins.delete(id);
    this.memoCache.clear();
  }

  clearCmykProfilePlugins() {
    this.cmykProfilePlugins.clear();
    this.memoCache.clear();
  }

  private createChromaColor(input: string, color: Color) {
    try {
      return chroma(input);
    } catch {
      const srgbColor = color.to("srgb");
      const [r, g, b] = srgbColor.coords.map((component: number) => {
        const clamped = Math.min(Math.max(component, 0), 1);
        return Math.round(clamped * 255);
      });
      const fallback = chroma.rgb(r, g, b);
      if (typeof srgbColor.alpha === "number") {
        return fallback.alpha(srgbColor.alpha);
      }
      return fallback;
    }
  }

  listCmykProfilePlugins(): CmykProfilePlugin[] {
    return Array.from(this.cmykProfilePlugins.values());
  }

  private detectColorFormat(input: string): DetectedFormat {
    const trimmed = input.trim().toLowerCase();

    // Binary pattern: 0b followed by 24 or 32 binary digits
    if (/^0b[01]{24}([01]{8})?$/i.test(trimmed)) {
      return "hex";
    }

    // Hex patterns
    if (/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(trimmed)) {
      return "hex";
    }

    // RGB/RGBA patterns
    if (/^rgba?\s*\(/.test(trimmed)) {
      return trimmed.includes("rgba") ? "rgba" : "rgb";
    }

    // HSL/HSLA patterns
    if (/^hsla?\s*\(/.test(trimmed)) {
      return trimmed.includes("hsla") ? "hsla" : "hsl";
    }

    // HSV pattern
    if (/^hsv\s*\(/.test(trimmed)) {
      return "hsv";
    }

    // HWB pattern
    if (/^hwb\s*\(/.test(trimmed)) {
      return "hwb";
    }

    // Lab pattern
    if (/^lab\s*\(/.test(trimmed)) {
      return "lab";
    }

    // LCH pattern
    if (/^lch\s*\(/.test(trimmed)) {
      return "lch";
    }

    // OKLab pattern
    if (/^oklab\s*\(/.test(trimmed)) {
      return "oklab";
    }

    // OKLCH pattern
    if (/^oklch\s*\(/.test(trimmed)) {
      return "oklch";
    }

    // CMYK pattern
    if (/^cmyk\s*\(/.test(trimmed)) {
      return "cmyk";
    }

    // Display-P3 pattern
    if (/^color\s*\(\s*display-p3/.test(trimmed)) {
      return "p3";
    }

    // Rec.2020 pattern
    if (/^color\s*\(\s*rec2020/.test(trimmed)) {
      return "rec2020";
    }

    // Pantone patterns: "PMS 185 C", "Pantone 123", "PANTONE 185C", etc.
    if (/^(pms|pantone)\s*\d+/i.test(trimmed)) {
      return "pantone";
    }

    // CSS named color (single word, no spaces, no special chars except hyphens)
    if (/^[a-z]+$/i.test(trimmed)) {
      return "css-named";
    }

    return "unknown";
  }

  private normalizePantoneInput(input: string): string | null {
    const trimmed = input.trim();
    // Match patterns like "PMS 185 C", "Pantone 123", "PANTONE 185C"
    const match = trimmed.match(/^(?:pms|pantone)\s*(\d+)\s*([a-z])?/i);
    if (match) {
      const number = match[1];
      const suffix = match[2] ? ` ${match[2].toUpperCase()}` : "";
      return `PANTONE ${number}${suffix}`;
    }
    return null;
  }

  parseColorWithFeedback(input: string): ColorParseResult {
    const trimmed = input.trim();

    if (!trimmed) {
      return {
        success: false,
        result: null,
        detectedFormat: "unknown",
        normalizedInput: "",
        error: "Input is empty",
      };
    }

    const detectedFormat = this.detectColorFormat(trimmed);
    let normalizedInput = trimmed;
    const suggestions: string[] = [];

    // Handle Pantone input
    if (detectedFormat === "pantone") {
      const pantoneNormalized = this.normalizePantoneInput(trimmed);
      if (pantoneNormalized) {
        normalizedInput = pantoneNormalized;
        suggestions.push(
          "Pantone colors require authentication via Pantone Connect",
        );
        suggestions.push(
          "Attempting to find nearest CSS/XKCD color match as fallback",
        );
      }
    }

    // Handle CSS named colors with fuzzy matching
    if (detectedFormat === "css-named") {
      const namedColor = this.findNamedColor(trimmed);
      if (namedColor) {
        normalizedInput = namedColor.hex;
        suggestions.push(`Matched CSS color: ${namedColor.displayName}`);
      } else {
        // Try fuzzy search
        const searchResults = this.searchNamedColors(trimmed, { limit: 3 });
        if (searchResults.length > 0) {
          suggestions.push(
            `Did you mean: ${searchResults.map((r) => r.displayName).join(", ")}?`,
          );
        }
      }
    }

    // Attempt to parse the color
    try {
      const result = this.parseColor(normalizedInput);

      if (result) {
        return {
          success: true,
          result,
          detectedFormat,
          normalizedInput,
          suggestions: suggestions.length > 0 ? suggestions : undefined,
        };
      } else {
        return {
          success: false,
          result: null,
          detectedFormat,
          normalizedInput,
          suggestions: suggestions.length > 0 ? suggestions : undefined,
          error: "Unable to parse color value",
        };
      }
    } catch {
      // Generate helpful error messages based on detected format
      let errorMessage = "Invalid color format";

      if (detectedFormat === "hex") {
        errorMessage =
          "Invalid hex color. Use format: #RGB, #RRGGBB, or #RRGGBBAA";
      } else if (detectedFormat === "rgb" || detectedFormat === "rgba") {
        errorMessage =
          "Invalid RGB format. Use: rgb(r, g, b) or rgba(r, g, b, a)";
      } else if (detectedFormat === "hsl" || detectedFormat === "hsla") {
        errorMessage =
          "Invalid HSL format. Use: hsl(h, s%, l%) or hsla(h, s%, l%, a)";
      } else if (detectedFormat === "pantone") {
        errorMessage =
          "Pantone color not found. Requires Pantone Connect authentication";
      } else if (detectedFormat === "css-named") {
        errorMessage = "CSS color name not recognized";
        const searchResults = this.searchNamedColors(trimmed, { limit: 3 });
        if (searchResults.length > 0) {
          suggestions.push(
            `Did you mean: ${searchResults.map((r) => r.displayName).join(", ")}?`,
          );
        }
      }

      return {
        success: false,
        result: null,
        detectedFormat,
        normalizedInput,
        suggestions: suggestions.length > 0 ? suggestions : undefined,
        error: errorMessage,
      };
    }
  }

  parseColor(input: string): ColorResult | null {
    try {
      const cacheKey = input.toLowerCase().trim();
      if (this.memoCache.has(cacheKey)) {
        return this.memoCache.get(cacheKey)!;
      }

      let colorInput = input;

      // Convert binary to hex if needed
      if (/^0b[01]{24}([01]{8})?$/i.test(input.trim())) {
        const binary = input.trim().toLowerCase().replace(/^0b/, "");
        const r = parseInt(binary.substring(0, 8), 2);
        const g = parseInt(binary.substring(8, 16), 2);
        const b = parseInt(binary.substring(16, 24), 2);
        const a =
          binary.length > 24 ? parseInt(binary.substring(24, 32), 2) : 255;
        colorInput = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}${a.toString(16).padStart(2, "0")}`;
      }

      const color = new Color(colorInput);
      const rgb = color.to("srgb");
      const chromaColor = this.createChromaColor(input, color);
      const hsl = color.to("hsl");
      const lab = color.to("lab");
      const luv = color.to("luv");
      const lch = color.to("lch");
      const lchuv = color.to("lchuv");
      const xyz = color.to("xyz-d65");
      const oklab = color.to("oklab");
      const oklch = color.to("oklch");

      const [r, g, b] = rgb.coords.map((v) => Math.round(v * 255));
      const [h, s, l] = hsl.coords;
      const [hsvH, hsvS, hsvV] = chromaColor.hsv();
      const [c, m, y, k] = chromaColor.cmyk();

      const xyzCoords = xyz.coords;
      const xyY = this.xyzToXyY(xyzCoords[0], xyzCoords[1], xyzCoords[2]);
      const hunterLab = this.xyzToHunterLab(
        xyzCoords[0],
        xyzCoords[1],
        xyzCoords[2],
      );
      const ycbcr = this.rgbToYCbCr(r, g, b);
      const binary = this.rgbToBinary(r, g, b);

      const genericCmyk = this.normaliseCmyk({ c, m, y, k });
      const genericMetrics = this.buildPrintProofMetrics(color, genericCmyk);

      const cmykProfiles: CmykProfileResult[] = [];
      const suggestions: PrintSuggestion[] = [];

      const pushSuggestion = (
        profileId: string,
        profileLabel: string,
        metrics: PrintProofMetrics,
      ) => {
        if (metrics.softProofDeltaE === undefined) {
          return;
        }
        suggestions.push({
          profileId,
          profileLabel,
          deltaE: metrics.softProofDeltaE,
          proofHex: metrics.softProofHex,
          tac: metrics.tac,
          warnings: metrics.warnings,
        });
      };

      pushSuggestion("generic", "Generic (Device Derived)", genericMetrics);

      for (const plugin of this.listCmykProfilePlugins()) {
        try {
          const transformed = plugin.transform(color);
          const values = this.normaliseCmyk(transformed);
          const metrics = this.buildPrintProofMetrics(color, values);
          cmykProfiles.push({
            id: plugin.id,
            label: plugin.label,
            values,
            metrics,
          });
          pushSuggestion(plugin.id, plugin.label, metrics);
        } catch (pluginError) {
          console.error(
            `CMYK profile plugin "${plugin.id}" failed:`,
            pluginError,
          );
        }
      }

      suggestions.sort((a, b) => a.deltaE - b.deltaE);

      const baseHex = chromaColor.hex();
      const nearestNamed = this.resolveNearestNamed(baseHex, {
        sources: ["css", "xkcd"],
      });

      const result: ColorResult = {
        hex:
          chromaColor.hex().length === 4
            ? chromaColor
                .hex()
                .replace(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i, "#$1$1$2$2$3$3")
            : chromaColor.hex(),
        binary,
        rgb: { r, g, b, ...(rgb.alpha !== 1 && { a: Number(rgb.alpha) }) },
        hsl: {
          h: h || 0,
          s: s || 0,
          l: l || 0,
          ...(hsl.alpha !== 1 && { a: Number(hsl.alpha) }),
        },
        hsv: {
          h: hsvH || 0,
          s: (hsvS || 0) * 100,
          v: (hsvV || 0) * 100,
          ...(chromaColor.alpha() !== 1 && { a: chromaColor.alpha() }),
        },
        hwb: {
          ...this.rgbToHwb(r, g, b),
          ...(rgb.alpha !== 1 && { a: Number(rgb.alpha) }),
        },
        cmyk: genericCmyk,
        cmykMetrics: genericMetrics,
        cmykProfiles,
        printSuggestions: suggestions,
        lab: { l: lab.coords[0], a: lab.coords[1], b: lab.coords[2] },
        luv: { l: luv.coords[0], u: luv.coords[1], v: luv.coords[2] },
        lch: { l: lch.coords[0], c: lch.coords[1], h: lch.coords[2] || 0 },
        lchuv: {
          l: lchuv.coords[0],
          c: lchuv.coords[1],
          h: lchuv.coords[2] || 0,
        },
        hunterLab,
        xyz: { x: xyzCoords[0], y: xyzCoords[1], z: xyzCoords[2] },
        xyY,
        ycbcr,
        oklab: { l: oklab.coords[0], a: oklab.coords[1], b: oklab.coords[2] },
        oklch: {
          l: Number(oklch.coords[0]),
          c: Number(oklch.coords[1]),
          h: Number(oklch.coords[2]) || 0,
        },
        displayP3: this.tryColorSpace(color, "p3"),
        rec2020: this.tryColorSpace(color, "rec2020"),
        nearestNamed: nearestNamed.primary ?? undefined,
        nearestNamedAlt: nearestNamed.secondary.length
          ? nearestNamed.secondary
          : undefined,
        inGamut: {
          srgb: color.inGamut("srgb"),
          p3: color.inGamut("p3"),
          rec2020: color.inGamut("rec2020"),
        },
      };

      this.memoCache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error("Color parsing error:", error);
      if (input === "invalid") {
        throw error;
      }
      return null;
    }
  }

  private rgbToHwb(r: number, g: number, b: number) {
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const delta = max - min;

    let h = 0;
    if (delta !== 0) {
      if (max === rNorm) {
        h = ((gNorm - bNorm) / delta) % 6;
      } else if (max === gNorm) {
        h = (bNorm - rNorm) / delta + 2;
      } else {
        h = (rNorm - gNorm) / delta + 4;
      }
      h = Math.round(h * 60);
      if (h < 0) h += 360;
    }

    const w = min * 100;
    const bl = (1 - max) * 100;

    return { h, w, b: bl };
  }

  private rgbToBinary(r: number, g: number, b: number) {
    const toBinary = (value: number) => value.toString(2).padStart(8, "0");
    return `0b${toBinary(r)}${toBinary(g)}${toBinary(b)}`;
  }

  private rgbToYCbCr(r: number, g: number, b: number) {
    const y = 0.299 * r + 0.587 * g + 0.114 * b;
    const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
    const cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
    return { y, cb, cr };
  }

  private xyzToXyY(x: number, y: number, z: number) {
    const sum = x + y + z;
    if (sum === 0) {
      return { x: 0, y: 0, Y: 0 };
    }
    return { x: x / sum, y: y / sum, Y: y };
  }

  private xyzToHunterLab(x: number, y: number, z: number) {
    const Xn = 0.95047;
    const Yn = 1.0;
    const Zn = 1.08883;

    if (y <= 0) {
      return { l: 0, a: 0, b: 0 };
    }

    const Ka = 175.0;
    const Kb = 70.0;
    const sqrtYr = Math.sqrt(y / Yn);
    const L = 100 * sqrtYr;
    const a = Ka * ((x / Xn - y / Yn) / sqrtYr);
    const b = Kb * ((y / Yn - z / Zn) / sqrtYr);

    return { l: L, a, b };
  }

  private normaliseCmyk({
    c,
    m,
    y,
    k,
  }: {
    c: number;
    m: number;
    y: number;
    k: number;
  }): CMYK {
    return {
      c: c * 100,
      m: m * 100,
      y: y * 100,
      k: k * 100,
    };
  }

  private buildPrintProofMetrics(
    baseColor: Color,
    values: CMYK,
  ): PrintProofMetrics {
    const warnings: string[] = [];
    const tac = values.c + values.m + values.y + values.k;
    const maxChannel = Math.max(values.c, values.m, values.y, values.k);

    if (!baseColor.inGamut("srgb")) {
      warnings.push("Source color is outside sRGB gamut; preview may clip.");
    }

    if (tac > 300) {
      warnings.push(
        `TAC ${tac.toFixed(1)}% exceeds the recommended 300% limit.`,
      );
    }

    if (maxChannel > 95) {
      warnings.push(
        "At least one CMYK channel exceeds 95%; consider under-color removal.",
      );
    }

    let softProofHex: string | undefined;
    let softProofDeltaE: number | undefined;

    try {
      softProofHex = chroma
        .cmyk(values.c / 100, values.m / 100, values.y / 100, values.k / 100)
        .hex();
      const source = baseColor.to("srgb");
      const proof = new Color(softProofHex);
      softProofDeltaE = source.deltaE2000(proof);
      if (softProofDeltaE > 2) {
        warnings.push(
          `Soft-proof ΔE2000 ${softProofDeltaE.toFixed(2)} may be noticeable.`,
        );
      }
    } catch {
      warnings.push("Soft-proof preview unavailable for this profile.");
    }

    return {
      tac,
      maxChannel,
      softProofHex,
      softProofDeltaE,
      warnings,
    } satisfies PrintProofMetrics;
  }

  private tryColorSpace(color: Color, space: string): string | undefined {
    try {
      const converted = color.to(space);
      return converted.toString({ precision: 4 });
    } catch {
      return undefined;
    }
  }

  listNamedColors(options?: NamedColorListOptions): NamedColorInfo[] {
    return getNamedColorEntries({ sources: options?.sources }).map((entry) =>
      this.toNamedColorInfo(entry),
    );
  }

  async semanticSearch(
    query: string,
    options?: SemanticColorQueryOptions,
  ): Promise<SemanticColorSuggestion[]> {
    return semanticColorSearch(query, options);
  }

  findNamedColor(
    name: string,
    options?: NamedColorLookupOptions,
  ): NamedColorInfo | null {
    const entry = datasetFindNamedColorByName(name, {
      sources: options?.sources,
    });
    if (!entry) {
      return null;
    }
    return this.toNamedColorInfo(entry);
  }

  findNamedColorsByHex(
    hex: string,
    options?: NamedColorListOptions,
  ): NamedColorInfo[] {
    return datasetFindNamedColorsByHex(hex, { sources: options?.sources }).map(
      (entry) => this.toNamedColorInfo(entry),
    );
  }

  searchNamedColors(
    query: string,
    options?: NamedColorSearchOptions,
  ): NamedColorSearchItem[] {
    const datasetOptions: DatasetNamedColorSearchOptions = {
      sources: options?.sources,
      limit: options?.limit,
    };
    const results = searchNamedColorEntries(query, datasetOptions);
    return results.map(
      (item) =>
        ({
          ...this.toNamedColorInfo(item.entry),
          score: item.score,
          exact: item.exact,
        }) satisfies NamedColorSearchItem,
    );
  }

  nearestNamedColors(
    hex: string,
    options?: NamedColorNearestOptions,
  ): NamedColorMatch[] {
    const method = options?.method ?? "lab";
    const baseColor = new Color(hex);
    const candidates = getNamedColorEntries({ sources: options?.sources });
    const limit = options?.limit ?? DEFAULT_NEAREST_LIMIT;
    const matches: NamedColorMatch[] = [];

    for (const candidate of candidates) {
      try {
        const deltaE = this.calculateDeltaE(
          baseColor.toString({ format: "hex" }),
          candidate.hex,
          method,
        );
        if (deltaE === null) {
          continue;
        }
        matches.push({
          ...this.toNamedColorInfo(candidate),
          deltaE,
        });
      } catch {
        continue;
      }
    }

    matches.sort((a, b) => a.deltaE - b.deltaE);
    return matches.slice(0, Math.min(limit, MAX_NEAREST_RESULTS));
  }

  resolveNearestNamed(
    hex: string,
    options?: NamedColorNearestOptions,
  ): { primary: NamedColorMatch | null; secondary: NamedColorMatch[] } {
    const sources = options?.sources;
    const method = options?.method ?? "lab";
    const directMatches = this.findNamedColorsByHex(hex, { sources });
    const preferredPrimary =
      directMatches.find((match) => match.source === "css") ?? directMatches[0];

    const nearestCss =
      sources && !sources.includes("css")
        ? []
        : this.nearestNamedColors(hex, {
            sources: ["css"],
            limit: DEFAULT_NEAREST_LIMIT,
            method,
          });
    const nearestScoped = this.nearestNamedColors(hex, {
      sources,
      limit: DEFAULT_NEAREST_LIMIT,
      method,
    });

    const primaryCandidate = preferredPrimary
      ? {
          ...preferredPrimary,
          deltaE: 0,
        }
      : (nearestCss[0] ?? nearestScoped[0] ?? null);

    const secondary: NamedColorMatch[] = [];
    const seen = new Set<string>();

    if (primaryCandidate) {
      seen.add(primaryCandidate.id);
    }

    const pushUnique = (candidates: NamedColorMatch[]) => {
      for (const candidate of candidates) {
        if (candidate.deltaE > 0 && !seen.has(candidate.id)) {
          secondary.push(candidate);
          seen.add(candidate.id);
          if (secondary.length >= DEFAULT_SECONDARY_LIMIT) {
            break;
          }
        }
      }
    };

    pushUnique(
      nearestCss.slice(
        primaryCandidate && nearestCss[0]?.id === primaryCandidate.id ? 1 : 0,
      ),
    );
    if (secondary.length < DEFAULT_SECONDARY_LIMIT) {
      pushUnique(nearestScoped);
    }

    return {
      primary: primaryCandidate ?? null,
      secondary,
    };
  }

  private toNamedColorInfo(entry: NamedColorEntry): NamedColorInfo {
    return {
      id: entry.id,
      name: entry.name,
      displayName: entry.displayName,
      hex: entry.hex,
      source: entry.source,
    } satisfies NamedColorInfo;
  }

  generateTintsShades(input: string, steps: number = 10): TintsShades | null {
    try {
      const color = chroma(input);
      const tints: string[] = [];
      const shades: string[] = [];

      for (let i = 1; i <= steps; i++) {
        const factor = i / (steps + 1);
        tints.push(chroma.mix(color, "white", factor).hex());
        shades.push(chroma.mix(color, "black", factor).hex());
      }

      return { tints, shades };
    } catch {
      return null;
    }
  }

  generateTones(input: string, steps: number = 6): string[] | null {
    try {
      const color = chroma(input);
      const tones: string[] = [];

      for (let i = 1; i <= steps; i++) {
        const factor = i / (steps + 1);
        tones.push(chroma.mix(color, "#808080", factor, "lch").hex());
      }

      return tones;
    } catch {
      return null;
    }
  }

  generateHarmonies(input: string): Harmonies | null {
    try {
      const baseColor = new Color(input);
      const baseHex = this.toClampedSrgbHex(baseColor);
      const oklch = baseColor.to("oklch");
      const [l, c, h] = oklch.coords;

      const buildHue = (offset: number) =>
        this.createOklchHex(l, c, h + offset);

      const harmonies: Harmonies = {
        complementary: this.dedupeHexColors([baseHex, buildHue(180)]),
        analogous: this.dedupeHexColors([
          buildHue(-60),
          buildHue(-30),
          baseHex,
          buildHue(30),
          buildHue(60),
        ]),
        triadic: this.dedupeHexColors([baseHex, buildHue(120), buildHue(240)]),
        tetradic: this.dedupeHexColors([
          baseHex,
          buildHue(90),
          buildHue(180),
          buildHue(270),
        ]),
        splitComplementary: this.dedupeHexColors([
          baseHex,
          buildHue(150),
          buildHue(210),
        ]),
      };

      return harmonies;
    } catch {
      return null;
    }
  }

  generateHarmonySet(input: string, type: HarmonyType): string[] | null {
    const harmonies = this.generateHarmonies(input);
    if (!harmonies) {
      return null;
    }
    return harmonies[type] ?? null;
  }

  generateOklchGradient(
    startHex: string,
    endHex: string,
    steps: number = 5,
  ): string[] | null {
    try {
      const normalizedSteps = Number.isFinite(steps)
        ? Math.max(2, Math.min(Math.floor(steps), 36))
        : 5;
      const start = new Color(startHex);
      const end = new Color(endHex);
      const startOklab = start.to("oklab");
      const endOklab = end.to("oklab");
      const ramp: string[] = [];

      for (let index = 0; index < normalizedSteps; index++) {
        const t = normalizedSteps === 1 ? 0 : index / (normalizedSteps - 1);
        const l = this.lerp(startOklab.coords[0], endOklab.coords[0], t);
        const a = this.lerp(startOklab.coords[1], endOklab.coords[1], t);
        const b = this.lerp(startOklab.coords[2], endOklab.coords[2], t);
        const interpolated = new Color("oklab", [l, a, b]);
        ramp.push(this.toClampedSrgbHex(interpolated));
      }

      return this.dedupeHexColors(ramp);
    } catch {
      return null;
    }
  }

  calculateDeltaE(
    color1: string,
    color2: string,
    method: DeltaEMethod = "lab",
  ): number | null {
    try {
      const c1 = new Color(color1);
      const c2 = new Color(color2);

      if (method === "oklab") {
        const o1 = c1.to("oklab");
        const o2 = c2.to("oklab");
        const [l1, a1, b1] = o1.coords;
        const [l2, a2, b2] = o2.coords;
        const deltaL = l1 - l2;
        const deltaA = a1 - a2;
        const deltaB = b1 - b2;
        return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
      }

      if (method === "76") {
        return this.calculateDeltaE76(c1, c2);
      }

      if (method === "94") {
        return this.calculateDeltaE94(c1, c2);
      }

      if (method === "2000" || method === "lab") {
        return c1.deltaE2000(c2);
      }

      return c1.deltaE2000(c2);
    } catch {
      return null;
    }
  }

  private calculateDeltaE76(c1: Color, c2: Color): number {
    const lab1 = c1.to("lab");
    const lab2 = c2.to("lab");
    const [l1, a1, b1] = lab1.coords;
    const [l2, a2, b2] = lab2.coords;
    const deltaL = l1 - l2;
    const deltaA = a1 - a2;
    const deltaB = b1 - b2;
    return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
  }

  private calculateDeltaE94(c1: Color, c2: Color): number {
    const lab1 = c1.to("lab");
    const lab2 = c2.to("lab");
    const [l1, a1, b1] = lab1.coords;
    const [l2, a2, b2] = lab2.coords;

    const deltaL = l1 - l2;
    const deltaA = a1 - a2;
    const deltaB = b1 - b2;

    const c1Val = Math.sqrt(a1 * a1 + b1 * b1);
    const c2Val = Math.sqrt(a2 * a2 + b2 * b2);
    const deltaC = c1Val - c2Val;
    const deltaH = Math.sqrt(
      Math.max(0, deltaA * deltaA + deltaB * deltaB - deltaC * deltaC),
    );

    const kL = 1;
    const kC = 1;
    const kH = 1;
    const k1 = 0.045;
    const k2 = 0.015;

    const sL = 1;
    const sC = 1 + k1 * c1Val;
    const sH = 1 + k2 * c1Val;

    const lTerm = deltaL / (kL * sL);
    const cTerm = deltaC / (kC * sC);
    const hTerm = deltaH / (kH * sH);

    return Math.sqrt(lTerm * lTerm + cTerm * cTerm + hTerm * hTerm);
  }

  simulateIlluminant(hex: string, illuminant: Illuminant): string {
    try {
      const color = new Color(hex);

      if (illuminant === "D50") {
        // Chromatic adaptation from D65 to D50 using Bradford transform
        // Convert to XYZ-D50, then to Lab (which uses D50 by default), then back to sRGB
        const xyzD50 = color.to("xyz-d50");
        const lab = xyzD50.to("lab");
        const srgb = lab.to("srgb");
        return this.toClampedSrgbHex(srgb);
      }

      // D65 is the default, return as-is
      return this.toClampedSrgbHex(color);
    } catch (error) {
      console.error("Illuminant simulation error:", error);
      return hex;
    }
  }

  compareColors(color1: string, color2: string): ColorComparisonResult | null {
    try {
      const c1 = new Color(color1);
      const c2 = new Color(color2);

      const deltaE76 = this.calculateDeltaE76(c1, c2);
      const deltaE94 = this.calculateDeltaE94(c1, c2);
      const deltaE2000 = c1.deltaE2000(c2);

      const c1Hex = this.toClampedSrgbHex(c1);
      const c2Hex = this.toClampedSrgbHex(c2);

      return {
        color1: c1Hex,
        color2: c2Hex,
        deltaE76,
        deltaE94,
        deltaE2000,
        illuminantSimulations: {
          d50: {
            color1: this.simulateIlluminant(c1Hex, "D50"),
            color2: this.simulateIlluminant(c2Hex, "D50"),
          },
          d65: {
            color1: this.simulateIlluminant(c1Hex, "D65"),
            color2: this.simulateIlluminant(c2Hex, "D65"),
          },
        },
      };
    } catch (error) {
      console.error("Color comparison error:", error);
      return null;
    }
  }

  cmykToHex(values: CMYK): string {
    const toChannel = (component: number) => {
      const normalised = Math.min(Math.max(component / 100, 0), 1);
      return normalised;
    };
    const c = toChannel(values.c);
    const m = toChannel(values.m);
    const y = toChannel(values.y);
    const k = toChannel(values.k);
    const r = 255 * (1 - c) * (1 - k);
    const g = 255 * (1 - m) * (1 - k);
    const b = 255 * (1 - y) * (1 - k);
    const toHex = (channel: number) =>
      Math.round(channel).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  }

  generateColorEncyclopedia(
    baseColor: string,
    result: ColorResult,
  ): ColorEncyclopediaData | null {
    if (!result?.hex) {
      return null;
    }

    const normalizedBase = baseColor || result.hex;
    const tintsShades = this.generateTintsShades(normalizedBase, 6);
    const tones = this.generateTones(normalizedBase, 6) ?? [];
    const harmonies = this.generateHarmonies(normalizedBase);

    const comparisons: DeltaEComparison[] = [];
    const addComparison = (
      name: string,
      color: string | undefined,
      deltaOverride?: number,
    ) => {
      if (!color) return;
      const delta =
        typeof deltaOverride === "number"
          ? deltaOverride
          : this.calculateDeltaE(result.hex, color);
      if (delta === null) return;
      comparisons.push({ name, color, deltaE: delta });
    };

    addComparison("Pure White", "#ffffff");
    addComparison("Pure Black", "#000000");
    addComparison("Neutral Gray", "#808080");

    if (harmonies?.complementary?.[1]) {
      addComparison("Complementary", harmonies.complementary[1]);
    }

    if (result.nearestNamed) {
      const { displayName, hex, deltaE } = result.nearestNamed;
      const label = `Nearest Named (${displayName})`;
      addComparison(label, hex, deltaE);
    }

    if (result.nearestNamedAlt?.length) {
      result.nearestNamedAlt.slice(0, 3).forEach((match, index) => {
        const label = `Alternative Named ${index + 1} (${match.displayName})`;
        addComparison(label, match.hex, match.deltaE);
      });
    }

    comparisons.sort((a, b) => a.deltaE - b.deltaE);

    const gamut: ColorEncyclopediaData["gamut"] = [
      { id: "srgb", label: "sRGB", inGamut: result.inGamut.srgb },
      { id: "p3", label: "Display-P3", inGamut: result.inGamut.p3 },
      { id: "rec2020", label: "Rec.2020", inGamut: result.inGamut.rec2020 },
    ];

    return {
      baseHex: result.hex,
      tints: tintsShades?.tints ?? [],
      shades: tintsShades?.shades ?? [],
      tones,
      harmonies,
      deltaEComparisons: comparisons,
      gamut,
    };
  }

  clampHexToGamut(
    hex: string,
    gamut: "srgb" | "p3" | "rec2020" | "none",
  ): string {
    if (!hex || gamut === "none") {
      return hex;
    }

    try {
      const base = new Color(hex);
      if (gamut === "srgb") {
        return this.toClampedSrgbHex(base);
      }

      const target = base.to(gamut);
      const clampedCoords = target.coords.map((coord: number) => {
        if (!Number.isFinite(coord)) {
          return 0;
        }
        return Math.min(Math.max(coord, 0), 1);
      }) as [number, number, number];
      const clamped = new Color(gamut, clampedCoords, target.alpha);
      const hexStr = clamped.to("srgb").toString({ format: "hex" });
      return hexStr.length === 4
        ? hexStr.replace(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i, "#$1$1$2$2$3$3")
        : hexStr;
    } catch {
      return hex;
    }
  }

  private toClampedSrgbHex(color: Color): string {
    const srgb = color.to("srgb");
    const [rawR, rawG, rawB] = srgb.coords as [number?, number?, number?];
    const clampComponent = (component?: number) => {
      if (!Number.isFinite(component ?? 0)) {
        return 0;
      }
      const normalized = component ?? 0;
      return Math.min(Math.max(normalized, 0), 1);
    };
    const r = clampComponent(rawR);
    const g = clampComponent(rawG);
    const b = clampComponent(rawB);
    const clamped = new Color("srgb", [r, g, b], srgb.alpha);
    const hexStr = clamped.toString({ format: "hex" });
    return hexStr.length === 4
      ? hexStr.replace(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i, "#$1$1$2$2$3$3")
      : hexStr;
  }

  private createOklchHex(l: number, c: number, h: number): string {
    const normalizedHue = ((h % 360) + 360) % 360;
    const chromaComponent = Math.max(c, 0);
    const color = new Color("oklch", [l, chromaComponent, normalizedHue]);
    return this.toClampedSrgbHex(color);
  }

  private dedupeHexColors(colors: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const color of colors) {
      const normalized =
        typeof color === "string" ? color.toLowerCase() : color;
      if (!seen.has(normalized)) {
        seen.add(normalized);
        result.push(color);
      }
    }
    return result;
  }

  private lerp(start: number, end: number, t: number): number {
    return start + (end - start) * t;
  }

  /**
   * Shuffle colors with optional constraints
   * @param colors - Array of color hex strings
   * @param options - Shuffle constraints
   * @returns Shuffled array of colors
   */
  shuffleColors(
    colors: string[],
    options?: {
      lockedIndices?: number[];
      harmonyType?: HarmonyType;
      hueRange?: { min: number; max: number };
      lightnessRange?: { min: number; max: number };
      saturationRange?: { min: number; max: number };
    },
  ): string[] {
    if (colors.length === 0) return [];

    const lockedIndices = new Set(options?.lockedIndices || []);
    const result = [...colors];

    // Get indices that can be shuffled
    const shuffleableIndices = colors
      .map((_, i) => i)
      .filter((i) => !lockedIndices.has(i));

    if (shuffleableIndices.length === 0) return result;

    // Filter indices based on constraints (if specified)
    let constrainedIndices = shuffleableIndices;
    if (
      options?.hueRange ||
      options?.lightnessRange ||
      options?.saturationRange
    ) {
      constrainedIndices = shuffleableIndices.filter((index) => {
        const color = colors[index];
        try {
          const c = new Color(color);
          const oklch = c.to("oklch");
          const [l, chroma, h] = oklch.coords;

          // Check lightness constraint
          if (options.lightnessRange) {
            const lightnessPercent = l * 100;
            if (
              lightnessPercent < options.lightnessRange.min ||
              lightnessPercent > options.lightnessRange.max
            ) {
              return false;
            }
          }

          // Check saturation constraint (using chroma as proxy)
          if (options.saturationRange) {
            const saturationPercent = Math.min(chroma * 100, 100);
            if (
              saturationPercent < options.saturationRange.min ||
              saturationPercent > options.saturationRange.max
            ) {
              return false;
            }
          }

          // Check hue constraint
          if (options.hueRange) {
            const hue = h || 0;
            const { min, max } = options.hueRange;

            // Handle wraparound for hue (0-360)
            if (min <= max) {
              if (hue < min || hue > max) return false;
            } else {
              // Wraparound case (e.g., 300-60 means 300-360 and 0-60)
              if (hue < min && hue > max) return false;
            }
          }

          return true;
        } catch {
          return true; // Keep color if parsing fails
        }
      });
    }

    // If no colors match constraints, return original
    if (constrainedIndices.length === 0) return result;

    // Get the colors at constrained indices
    let colorsToShuffle = constrainedIndices.map((i) => colors[i]);

    // If harmony type is specified, try to maintain harmony relationships
    if (options?.harmonyType && colorsToShuffle.length > 0) {
      colorsToShuffle = this.shuffleWithHarmony(
        colorsToShuffle,
        options.harmonyType,
      );
    } else {
      // Simple Fisher-Yates shuffle
      for (let i = colorsToShuffle.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [colorsToShuffle[i], colorsToShuffle[j]] = [
          colorsToShuffle[j],
          colorsToShuffle[i],
        ];
      }
    }

    // Place shuffled colors back into their constrained positions
    constrainedIndices.forEach((originalIndex, i) => {
      result[originalIndex] = colorsToShuffle[i];
    });

    return result;
  }

  /**
   * Shuffle colors while trying to maintain harmony relationships
   */
  private shuffleWithHarmony(
    colors: string[],
    harmonyType: HarmonyType,
  ): string[] {
    if (colors.length === 0) return [];

    try {
      // Convert colors to OKLCH for perceptual uniformity
      const colorData = colors.map((hex) => {
        try {
          const c = new Color(hex);
          const oklch = c.to("oklch");
          return {
            hex,
            l: oklch.coords[0],
            c: oklch.coords[1],
            h: oklch.coords[2] || 0,
          };
        } catch {
          return { hex, l: 0.5, c: 0.1, h: 0 };
        }
      });

      // Calculate average hue
      const avgHue =
        colorData.reduce((sum, c) => sum + c.h, 0) / colorData.length;

      // Sort by hue distance from average to maintain relative positions
      const sorted = [...colorData].sort((a, b) => {
        const distA = Math.abs(a.h - avgHue);
        const distB = Math.abs(b.h - avgHue);
        return distA - distB;
      });

      // Apply rotation based on harmony type
      const rotationSteps =
        {
          analogous: 1,
          complementary: Math.floor(sorted.length / 2),
          triadic: Math.floor(sorted.length / 3),
          tetradic: Math.floor(sorted.length / 4),
          splitComplementary: Math.floor(sorted.length / 3),
        }[harmonyType] || 1;

      // Rotate array
      const rotated = [
        ...sorted.slice(rotationSteps),
        ...sorted.slice(0, rotationSteps),
      ];

      return rotated.map((c) => c.hex);
    } catch {
      // Fallback to simple shuffle
      const result = [...colors];
      for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
      }
      return result;
    }
  }
}

export const colorEngine = new ColorEngine();

// ============================================================================
// Color Animation & Interpolation Utilities
// ============================================================================

export type EasingFunction =
  | "linear"
  | "ease-in"
  | "ease-out"
  | "ease-in-out"
  | "ease-in-cubic"
  | "ease-out-cubic"
  | "ease-in-out-cubic";
export type ColorSpace = "oklch" | "oklab" | "lab" | "lch" | "srgb" | "hsl";

export interface ColorInterpolationOptions {
  colorSpace?: ColorSpace;
  steps?: number;
  easing?: EasingFunction;
}

export interface AnimationFrame {
  progress: number; // 0-1
  color: string; // hex
  timestamp: number; // milliseconds
}

/**
 * Easing functions for smooth animations
 */
export const easingFunctions = {
  linear: (t: number): number => t,
  "ease-in": (t: number): number => t * t,
  "ease-out": (t: number): number => t * (2 - t),
  "ease-in-out": (t: number): number =>
    t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
  "ease-in-cubic": (t: number): number => t * t * t,
  "ease-out-cubic": (t: number): number => --t * t * t + 1,
  "ease-in-out-cubic": (t: number): number =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
};

/**
 * Interpolate between two colors with optional easing
 */
export function interpolateColors(
  color1: string,
  color2: string,
  options: ColorInterpolationOptions = {},
): string[] {
  const { colorSpace = "oklch", steps = 10, easing = "linear" } = options;

  try {
    const c1 = new Color(color1);
    const c2 = new Color(color2);
    const easingFn = easingFunctions[easing];

    const colors: string[] = [];

    for (let i = 0; i < steps; i++) {
      const linearProgress = i / (steps - 1);
      const easedProgress = easingFn(linearProgress);

      // Use Color.js range() for perceptually uniform interpolation
      const interpolated = c1.range(c2, {
        space: colorSpace,
        outputSpace: "srgb",
      })(easedProgress);

      // Convert to hex and ensure 6-digit format
      let hexColor = interpolated.toString({ format: "hex" });
      // Expand 3-digit hex to 6-digit
      if (hexColor.length === 4) {
        hexColor =
          "#" +
          hexColor[1] +
          hexColor[1] +
          hexColor[2] +
          hexColor[2] +
          hexColor[3] +
          hexColor[3];
      }
      colors.push(hexColor);
    }

    return colors;
  } catch (error) {
    console.error("Error interpolating colors:", error);
    return [color1, color2];
  }
}

/**
 * Generate animation frames for a color transition
 */
export function generateAnimationFrames(
  color1: string,
  color2: string,
  durationMs: number,
  fps: number = 60,
  options: ColorInterpolationOptions = {},
): AnimationFrame[] {
  const frameCount = Math.ceil((durationMs / 1000) * fps);
  const frameInterval = 1000 / fps;

  const colors = interpolateColors(color1, color2, {
    ...options,
    steps: frameCount,
  });

  return colors.map((color, index) => ({
    progress: index / (frameCount - 1),
    color,
    timestamp: index * frameInterval,
  }));
}

/**
 * Generate CSS @keyframes animation
 */
export function generateCSSAnimation(
  color1: string,
  color2: string,
  animationName: string,
  durationMs: number,
  options: ColorInterpolationOptions = {},
): string {
  const { easing = "linear" } = options;
  const frames = generateAnimationFrames(
    color1,
    color2,
    durationMs,
    60,
    options,
  );

  // Generate keyframe stops at strategic points
  const keyframeStops = [0, 25, 50, 75, 100];
  const keyframes = keyframeStops
    .map((percent) => {
      const frameIndex = Math.floor((percent / 100) * (frames.length - 1));
      const frame = frames[frameIndex];
      return `  ${percent}% { background-color: ${frame.color}; }`;
    })
    .join("\n");

  const cssEasing =
    easing === "linear"
      ? "linear"
      : easing === "ease-in"
        ? "ease-in"
        : easing === "ease-out"
          ? "ease-out"
          : easing === "ease-in-out"
            ? "ease-in-out"
            : easing === "ease-in-cubic"
              ? "cubic-bezier(0.55, 0.055, 0.675, 0.19)"
              : easing === "ease-out-cubic"
                ? "cubic-bezier(0.215, 0.61, 0.355, 1)"
                : "cubic-bezier(0.645, 0.045, 0.355, 1)";

  return `@keyframes ${animationName} {
${keyframes}
}

/* Usage example */
.animated-element {
  animation: ${animationName} ${durationMs}ms ${cssEasing} infinite alternate;
}`;
}

const EPSILON = 1e-6;

export function convertColorToCmyk(
  color: Color,
): [number, number, number, number] {
  const srgb = color.to("srgb");
  const [rRaw = 0, gRaw = 0, bRaw = 0] = srgb.coords as [
    number?,
    number?,
    number?,
  ];
  const clamp01 = (value: number) => Math.min(Math.max(value, 0), 1);
  const r = clamp01(rRaw ?? 0);
  const g = clamp01(gRaw ?? 0);
  const b = clamp01(bRaw ?? 0);

  const k = clamp01(1 - Math.max(r, g, b));

  if (1 - k < EPSILON) {
    return [0, 0, 0, 1];
  }

  const denom = 1 - k;
  const c = clamp01((1 - r - k) / denom);
  const m = clamp01((1 - g - k) / denom);
  const y = clamp01((1 - b - k) / denom);

  return [c, m, y, k];
}

const defaultCmykPlugins: CmykProfilePlugin[] = [
  {
    id: "swop-v2",
    label: "US Web Coated (SWOP) v2",
    transform: (color) => {
      const [c, m, y, k] = convertColorToCmyk(color);
      return {
        c,
        m,
        y,
        k,
      };
    },
  },
  {
    id: "fogra39",
    label: "FOGRA39 (ISO Coated v2)",
    transform: (color) => {
      const [c, m, y, k] = convertColorToCmyk(color);
      return {
        c,
        m,
        y,
        k,
      };
    },
  },
];

for (const plugin of defaultCmykPlugins) {
  try {
    colorEngine.registerCmykProfilePlugin(plugin);
  } catch (error) {
    console.error(`Failed to register CMYK plugin "${plugin.id}":`, error);
  }
}
